import {
  CONTRACTOR_BADGE_VERIFICATION_FILTER_STATUS,
  getContractorBadgeVerificationsQuerySchema,
  type GetContractorBadgeVerificationsResponse,
  getContractorBadgeVerificationsResponseSchema,
} from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { type QueryClient, useQuery, type UseQueryResult } from "@tanstack/react-query";

import { CONTRACTOR_BADGE_VERIFICATIONS_URL } from "../constants";

interface GetQueryParamsOptions {
  includeFinalGrade?: boolean;
}

function getQueryParams(shiftId: string, options: GetQueryParamsOptions = {}) {
  return {
    filter: {
      shiftId: {
        eq: shiftId,
      },
      ...(options.includeFinalGrade
        ? {
            status: {
              eq: [CONTRACTOR_BADGE_VERIFICATION_FILTER_STATUS.GRADED],
            },
          }
        : {}),
    },
    ...(options.includeFinalGrade ? { include: ["finalGrade"] } : {}),
  };
}

export function getContractorBadgeVerificationQueryKey(
  shiftId: string,
  options: GetQueryParamsOptions = {}
) {
  return [CONTRACTOR_BADGE_VERIFICATIONS_URL, getQueryParams(shiftId, options)] as const;
}

export async function invalidateContractorBadgeVerification(
  queryClient: QueryClient,
  shiftId: string
): Promise<void> {
  await queryClient.invalidateQueries({
    queryKey: getContractorBadgeVerificationQueryKey(shiftId),
  });
}

interface UseGetContractorBadgeVerificationOptions {
  enabled?: boolean;
  includeFinalGrade?: boolean;
  refetchInterval?:
    | number
    | false
    | ((data: GetContractorBadgeVerificationsResponse | undefined) => number | false);
}

export function useGetContractorBadgeVerification(
  shiftId: string,
  options: UseGetContractorBadgeVerificationOptions = {}
): UseQueryResult<GetContractorBadgeVerificationsResponse> {
  const { enabled, includeFinalGrade, refetchInterval } = options;
  const queryParams = getQueryParams(shiftId, { includeFinalGrade });

  return useQuery({
    queryKey: getContractorBadgeVerificationQueryKey(shiftId, { includeFinalGrade }),
    queryFn: async () => {
      const response = await get({
        url: CONTRACTOR_BADGE_VERIFICATIONS_URL,
        queryParams,
        requestSchema: getContractorBadgeVerificationsQuerySchema,
        responseSchema: getContractorBadgeVerificationsResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.BADGE_VERIFICATION_GET_FAILURE,
    },
    ...(isDefined(enabled) ? { enabled } : {}),
    ...(isDefined(refetchInterval) ? { refetchInterval } : {}),
  });
}
